import Feature from 'ol/Feature'
import { reactive } from 'vue'
import { TRANSACTION_MAP_PARCEL_ZOOM } from '@/constants'

const TOOLTIP_SHOW_DELAY_MS = 120

/**
 * Pointermove handling for the transactions map (RED-255): pointer cursor over
 * anything clickable, an at-a-glance tooltip for markers (singles AND
 * clusters) and parcels, plus a canvas highlight layer that brings the
 * hovered marker forward (WebGL layers have no per-feature z-order) and
 * lights up every sibling parcel of the hovered deal.
 *
 * rAF-throttled — at most one hit-test per frame, nothing while dragging.
 * All tooltip data rides on the loaded feature properties; no network on hover.
 *
 * @param {object} options
 * @param {() => import('ol/Map').default|null} options.getMap
 * @param {(pixel: Array<number>) => import('ol/Feature').default|null} options.findMarkerAtPixel - JS hit-test (R-tree + icon box); never the GPU hit-detection path
 * @param {(id: string|number) => import('ol/Feature').default|undefined} options.findFeatureById
 * @param {(id: string|number) => import('ol/Feature').default|undefined} options.findClusterByMemberId - cluster containing a clustered-away transaction
 * @param {() => import('ol/source/Vector').default|null} options.getHighlightSource - canvas layer the hovered clones render on
 * @param {(id: string|number) => Array<import('ol/Feature').default>} options.getParcelsByTransactionId
 * @param {(coordinate: Array<number>) => Array<import('ol/Feature').default>} options.getParcelsAtCoordinate - geometric lookup (WebGL pixel hit-detection misses the near-transparent fill)
 */
export function useTransactionMapHover({
  getMap,
  findMarkerAtPixel,
  findFeatureById,
  findClusterByMemberId,
  getHighlightSource,
  getParcelsByTransactionId,
  getParcelsAtCoordinate,
}) {
  const tooltip = reactive({
    visible: false,
    x: 0,
    y: 0,
    price: null,
    area: null,
    areaUnit: null,
    date: null,
    address: null,
    objektiLiik: null,
    land: false,
    viewed: false,
    parcelCount: 0,
    // Cluster aggregates (RED-255): combined info instead of a blank hover
    isCluster: false,
    count: 0,
    medianLabel: null,
    dateMin: null,
    dateMax: null,
  })

  let rafPending = false
  let highlightedKey = null
  let showTimer = null
  // Last cursor value written to the map element — a DOM style write per rAF
  // is wasted work when nothing changed.
  let lastCursor = ''
  // Feature the tooltip fields were last filled from; only x/y track per-frame
  let tooltipSource = null

  const setCursor = (map, cursor) => {
    if (cursor !== lastCursor) {
      lastCursor = cursor
      const el = map.getTargetElement()
      if (el) {
        el.style.cursor = cursor
      }
    }
  }

  const hideTooltip = () => {
    clearTimeout(showTimer)
    showTimer = null
    tooltip.visible = false
    tooltipSource = null
  }

  const clearHighlight = () => {
    if (highlightedKey !== null) {
      getHighlightSource()?.clear()
      highlightedKey = null
    }
  }

  const clearHover = () => {
    clearHighlight()
    hideTooltip()
    const map = getMap()
    if (map) {
      setCursor(map, '')
    }
  }

  // The marker clone that renders enlarged on the highlight layer. iconKey
  // mirrors the WebGL style-entry keying (StyleService getWebGLIcons):
  // singles `${icon}[_land][_viewed]`, clusters carry the cache key directly.
  const markerHighlightFeature = marker => {
    const clone = new Feature({ geometry: marker.getGeometry().clone() })
    if (marker.get('cluster')) {
      const count = marker.get('point_count') || 0
      clone.set('iconKey', marker.get('icon') || 'cluster')
      clone.set('countLabel', count > 99 ? '99+' : String(count))
    } else {
      const icon = marker.get('icon') || 'default'
      const land = marker.get('land') === 1 ? '_land' : ''
      const viewed = marker.get('viewed') === 1 ? '_viewed' : ''
      clone.set('iconKey', `${icon}${land}${viewed}`)
    }
    return clone
  }

  const parcelClone = parcel => {
    const clone = new Feature({ geometry: parcel.getGeometry().clone() })
    clone.set('kind', 'parcel')
    return clone
  }

  const setHighlight = (key, marker, siblingParcels, parcel = null) => {
    if (highlightedKey === key) {
      return
    }
    const source = getHighlightSource()
    if (!source) {
      return
    }
    source.clear()
    highlightedKey = key

    const features = []
    if (marker) {
      features.push(markerHighlightFeature(marker))
    }
    // Every sibling parcel of the deal lights up together — a multi-parcel
    // purchase reads as one group, not disconnected polygons. Shared plots
    // (many transactions, e.g. an apartment building) highlight just the
    // hovered polygon itself.
    if (siblingParcels.length > 0) {
      siblingParcels.forEach(sibling => {
        features.push(parcelClone(sibling))
      })
    } else if (parcel) {
      features.push(parcelClone(parcel))
    }
    if (features.length > 0) {
      source.addFeatures(features)
    }
  }

  const fillTooltipFrom = feature => {
    const isCluster = !!feature.get('cluster')
    tooltip.isCluster = isCluster
    if (isCluster) {
      tooltip.count = feature.get('point_count') ?? 0
      tooltip.medianLabel = feature.get('median_hind_yhik') ?? null
      tooltip.dateMin = feature.get('date_min') ?? null
      tooltip.dateMax = feature.get('date_max') ?? null
      // Most specific address level every member shares (may be null)
      tooltip.address = feature.get('address') ?? null
      return
    }
    tooltip.price = feature.get('price') ?? null
    tooltip.area = feature.get('area') ?? null
    tooltip.areaUnit = feature.get('areaUnit') ?? null
    tooltip.date = feature.get('date') ?? null
    tooltip.address = feature.get('address') ?? null
    tooltip.objektiLiik = feature.get('objektiLiik') ?? null
    tooltip.land = feature.get('land') === 1
    tooltip.viewed = feature.get('viewed') === 1
  }

  const updateHover = (map, pixel) => {
    const marker = findMarkerAtPixel(pixel)

    // Parcels resolve geometrically: anywhere inside the polygon counts,
    // not just the stroke. Gated to the zooms the layer is visible at.
    const zoom = map.getView().getZoom()
    const parcel =
      zoom >= TRANSACTION_MAP_PARCEL_ZOOM
        ? getParcelsAtCoordinate(map.getCoordinateFromPixel(pixel))[0] ?? null
        : null

    setCursor(map, marker || parcel ? 'pointer' : '')

    if (!marker && !parcel) {
      clearHighlight()
      hideTooltip()
      return
    }

    // A parcel owned by exactly one transaction resolves to it; shared plots
    // (apartment buildings) and clustered-away owners resolve to the owners'
    // CLUSTER, so the parcel hover shows the same combined tooltip as
    // hovering the cluster marker itself.
    const parcelOwners = parcel?.get('transaction_ids') || []
    const soleParcelOwner = parcelOwners.length === 1 ? parcelOwners[0] : null

    // Tooltip target: the marker, or the marker behind a hovered parcel
    let target = marker ?? null
    const transactionId = marker?.get('cluster')
      ? null
      : (marker?.get('id') ?? soleParcelOwner)
    if (!target && parcelOwners.length > 0) {
      target =
        (soleParcelOwner != null ? findFeatureById(soleParcelOwner) : null) ??
        findClusterByMemberId(parcelOwners[0]) ??
        null
    }

    // One lookup serves both the highlight clones and the tooltip count
    const siblingParcels =
      transactionId != null ? getParcelsByTransactionId(transactionId) : []

    // Bring the hovered thing forward: marker clone + all sibling parcels.
    // Clusters have no id — key on their coordinates so adjacent clusters
    // with equal counts still switch the highlight.
    const markerKey =
      marker &&
      (marker.getId() ??
        marker.get('id') ??
        marker.getGeometry()?.getCoordinates?.()?.join(','))
    const highlightKey = marker
      ? `m:${markerKey}`
      : `p:${parcel.get('tunnus')}`
    setHighlight(highlightKey, marker ?? target, siblingParcels, parcel)

    if (!target) {
      hideTooltip()
      return
    }

    // Fields only change when the hovered feature does; x/y track every frame.
    // parcelCount self-heals separately: sibling parcels can land async while
    // the same target stays hovered.
    if (tooltipSource !== target) {
      tooltipSource = target
      fillTooltipFrom(target)
    }
    if (tooltip.parcelCount !== siblingParcels.length) {
      tooltip.parcelCount = siblingParcels.length
    }
    tooltip.x = pixel[0]
    tooltip.y = pixel[1]

    // Short show-delay avoids flicker while sweeping across markers
    if (!tooltip.visible && !showTimer) {
      showTimer = setTimeout(() => {
        showTimer = null
        tooltip.visible = true
      }, TOOLTIP_SHOW_DELAY_MS)
    }
  }

  const handlePointerMove = evt => {
    const map = getMap()
    if (!map) {
      return
    }
    if (evt.dragging) {
      clearHover()
      return
    }
    if (rafPending) {
      return
    }
    rafPending = true
    const pixel = evt.pixel
    requestAnimationFrame(() => {
      rafPending = false
      if (getMap()) {
        updateHover(map, pixel)
      }
    })
  }

  return {
    tooltip,
    handlePointerMove,
    clearHover,
  }
}
